import { useEffect, useState } from "react";
import api from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Edit3, Gift } from "lucide-react";
import { toast } from "sonner";

const emptyReward = { title: "", description: "", cost: 1000, category: "Consultation", active: true, stock: -1 };

export default function AdminRewardsPage() {
  const [rewards, setRewards] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyReward);
  const [editing, setEditing] = useState(null);

  const load = async () => setRewards((await api.get("/admin/rewards")).data);
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      const payload = { ...form, cost: Number(form.cost), stock: Number(form.stock) };
      if (editing) await api.put(`/admin/rewards/${editing}`, payload);
      else await api.post("/admin/rewards", payload);
      toast.success(editing ? "Reward updated" : "Reward added");
      setOpen(false); setEditing(null); setForm(emptyReward);
      await load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => { await api.delete(`/admin/rewards/${id}`); await load(); };

  const edit = (r) => { setEditing(r.id); setForm({ ...r }); setOpen(true); };

  return (
    <AdminShell title="Reward catalogue">
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(emptyReward); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-reward-btn" className="rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)]"><Plus className="h-4 w-4 mr-1" /> New reward</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display">{editing ? "Edit reward" : "New reward"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Title</Label><Input data-testid="rwd-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div><Label>Description</Label><Textarea data-testid="rwd-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Cost (pts)</Label><Input data-testid="rwd-cost" type="number" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} /></div>
                <div><Label>Category</Label><Input data-testid="rwd-cat" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
                <div><Label>Stock (-1 = ∞)</Label><Input data-testid="rwd-stock" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} /></div>
              </div>
              <div className="flex items-center gap-3"><Switch data-testid="rwd-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Active</Label></div>
            </div>
            <DialogFooter><Button data-testid="rwd-save-btn" onClick={submit} className="rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)]">{editing ? "Update" : "Create"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {rewards.map((r) => (
          <div key={r.id} className="bg-white border border-mr rounded-2xl p-6">
            <div className="flex items-start justify-between mb-3">
              <div className="h-10 w-10 rounded-xl bg-[#E2E8F0] flex items-center justify-center"><Gift className="h-5 w-5 text-mr-accent" /></div>
              <div className="flex gap-2">
                <Badge variant="outline" className="rounded-full border-mr">{r.category}</Badge>
                {!r.active && <Badge className="rounded-full bg-mr-secondary text-white">Inactive</Badge>}
              </div>
            </div>
            <h3 className="font-display text-lg font-semibold text-mr-primary">{r.title}</h3>
            <p className="text-mr-secondary text-sm mt-1 line-clamp-3">{r.description}</p>
            <div className="mt-4 flex items-center justify-between">
              <p className="font-display text-xl font-bold text-mr-primary">{r.cost.toLocaleString()} <span className="text-xs font-normal text-mr-secondary">pts</span></p>
              <div className="flex gap-1">
                <Button data-testid={`rwd-edit-${r.id}`} variant="ghost" size="icon" onClick={() => edit(r)}><Edit3 className="h-4 w-4 text-mr-primary" /></Button>
                <Button data-testid={`rwd-del-${r.id}`} variant="ghost" size="icon" onClick={() => remove(r.id)}><Trash2 className="h-4 w-4 text-mr-accent" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </AdminShell>
  );
}
